import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Monitor, Smartphone, ShieldCheck, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';

function StatusRow({ label, ok, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between py-2 border-b border-white/10 last:border-0"
    >
      <span className="text-sm text-white/75">{label}</span>
      {ok ? (
        <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-semibold">
          <CheckCircle2 className="w-3.5 h-3.5" /> OK
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-amber-300 text-xs font-semibold">
          <AlertCircle className="w-3.5 h-3.5" /> NEE
        </span>
      )}
    </div>
  );
}

/**
 * /admin/install — afgeschermde install-landing voor Powersure Control Center.
 *
 * Niet publiek vindbaar (noindex), geen klant-facing nav-link, vereist
 * bestaand `admin_token` in localStorage. Toont PWA install-instructie +
 * statuscheck van manifest / standalone / service worker.
 *
 * Veiligheid:
 *  - geen auth wijziging
 *  - geen nieuwe rechten
 *  - geen secrets / PII in DOM
 *  - geen tracking met PII
 *  - geen externe integratie
 *  - alleen leesoperaties op browser-state
 */
export default function AdminInstallPage() {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('admin_token'));
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installState, setInstallState] = useState('idle'); // idle | prompting | accepted | dismissed | unavailable
  const [diag, setDiag] = useState({
    standalone: false,
    sw_available: false,
    sw_registered: false,
    manifest_href: '',
    is_admin_route: false,
    ua_class: 'desktop',
  });

  // Auth gate — same pattern as AdminPage
  useEffect(() => {
    if (!token) {
      navigate('/admin/login?next=/admin/install', { replace: true });
    }
  }, [token, navigate]);

  // Set document title + noindex meta dynamically (no helmet dependency)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Powersure Control Center — Installeren (intern)';

    const ensureMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"][data-admin-install="1"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('data-admin-install', '1');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    const ensureHttpEquivMeta = (httpEquiv, content) => {
      let el = document.querySelector(`meta[http-equiv="${httpEquiv}"][data-admin-install="1"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('http-equiv', httpEquiv);
        el.setAttribute('data-admin-install', '1');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    const robots = ensureMeta('robots', 'noindex, nofollow, noarchive');
    const googlebot = ensureMeta('googlebot', 'noindex, nofollow');
    const cache = ensureHttpEquivMeta('Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0');
    const pragma = ensureHttpEquivMeta('Pragma', 'no-cache');
    return () => {
      document.title = prevTitle;
      [robots, googlebot, cache, pragma].forEach((el) => el && el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);

  // Browser diagnostics (read-only)
  useEffect(() => {
    try {
      const isStandalone =
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        window.navigator?.standalone === true;
      const swAvailable = 'serviceWorker' in navigator;
      const manifestLink = document.querySelector('link[rel="manifest"]');
      const manifestHref = manifestLink?.getAttribute('href') || '';
      const path = window.location.pathname || '';
      const ua = (navigator.userAgent || '').toLowerCase();
      const uaClass = /iphone|ipad|ipod/.test(ua)
        ? 'ios'
        : /android/.test(ua)
        ? 'android'
        : 'desktop';

      setDiag({
        standalone: isStandalone,
        sw_available: swAvailable,
        sw_registered: false,
        manifest_href: manifestHref,
        is_admin_route: path === '/admin' || path.startsWith('/admin/'),
        ua_class: uaClass,
      });

      if (swAvailable && navigator.serviceWorker.getRegistration) {
        navigator.serviceWorker.getRegistration().then((reg) => {
          setDiag((d) => ({ ...d, sw_registered: !!reg }));
        }).catch(() => {});
      }
    } catch (e) { /* no-op */ }
  }, []);

  // Capture install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const onInstalled = () => setInstallState('accepted');
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const onInstallClick = async () => {
    if (!deferredPrompt) {
      setInstallState('unavailable');
      return;
    }
    setInstallState('prompting');
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      setInstallState(outcome === 'accepted' ? 'accepted' : 'dismissed');
    } catch (e) {
      setInstallState('dismissed');
    } finally {
      setDeferredPrompt(null);
    }
  };

  const manifestIsAdmin = useMemo(
    () => (diag.manifest_href || '').includes('manifest-admin'),
    [diag.manifest_href]
  );

  if (!token) {
    // While redirecting render nothing (defense in depth — no admin content visible)
    return null;
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background:
          'radial-gradient(120% 80% at 100% 0%, rgba(15,61,56,0.35) 0%, rgba(15,61,56,0) 55%), linear-gradient(180deg, #0a1410 0%, #050a08 100%)',
      }}
      data-testid="admin-install-page"
    >
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-10">
        <button
          type="button"
          onClick={() => navigate('/admin?app=owner')}
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-emerald-300 transition-colors mb-6"
          data-testid="admin-install-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Terug naar admin dashboard
        </button>

        <div className="mb-2 text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">
          Intern · alleen admin/owner
        </div>
        <h1
          className="text-3xl sm:text-4xl font-semibold mb-3"
          style={{ fontFamily: 'Outfit' }}
          data-testid="admin-install-title"
        >
          Powersure Control Center installeren
        </h1>
        <p className="text-sm text-white/65 leading-relaxed mb-8 max-w-prose">
          Installeer Powersure Control Center op je apparaat voor snelle, eigen toegang.
          Deze versie is uitsluitend voor intern gebruik (admin/owner). Niet delen met klanten.
          Geen publieke Play Store-listing.
        </p>

        {/* Install card */}
        <div
          className="rounded-2xl border border-emerald-400/30 p-5 sm:p-6 mb-6"
          style={{
            background:
              'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)',
          }}
          data-testid="admin-install-card"
        >
          <div className="flex items-start gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0">
              <Download className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold text-white">PWA install</div>
              <div className="text-xs text-white/65 leading-snug mt-0.5">
                Direct via je browser — werkt op desktop, Android en (beperkt) iOS Safari.
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onInstallClick}
            data-testid="admin-install-prompt-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-900/40"
            disabled={installState === 'prompting'}
          >
            <Download className="w-3.5 h-3.5" />
            {installState === 'prompting' ? 'Bezig…' : 'Installeren op dit apparaat'}
          </button>

          {installState === 'accepted' && (
            <p className="text-xs text-emerald-300 mt-3" data-testid="admin-install-state-accepted">
              ✔ Installatie geaccepteerd. Open Powersure Control Center via je startscherm.
            </p>
          )}
          {installState === 'dismissed' && (
            <p className="text-xs text-amber-300 mt-3" data-testid="admin-install-state-dismissed">
              Installatie geannuleerd. Je kunt later opnieuw proberen via de browser-menu install-optie.
            </p>
          )}
          {installState === 'unavailable' && (
            <p className="text-xs text-amber-300 mt-3" data-testid="admin-install-state-unavailable">
              Geen automatische install-prompt beschikbaar. Volg de browser-instructie hieronder.
            </p>
          )}
        </div>

        {/* Browser instructies per platform */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="rounded-xl border border-white/10 p-4" data-testid="admin-install-instr-desktop">
            <div className="flex items-center gap-2 mb-2">
              <Monitor className="w-4 h-4 text-emerald-300" />
              <div className="text-sm font-semibold text-white">Desktop</div>
            </div>
            <p className="text-xs text-white/65 leading-relaxed">
              Chrome / Edge: adresbalk → install-icoon (rechts) → &ldquo;Installeren&rdquo;.
              Brave: menu → &ldquo;Installeer Powersure Control Center&rdquo;.
            </p>
          </div>
          <div className="rounded-xl border border-white/10 p-4" data-testid="admin-install-instr-android">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-4 h-4 text-emerald-300" />
              <div className="text-sm font-semibold text-white">Android</div>
            </div>
            <p className="text-xs text-white/65 leading-relaxed">
              Chrome → 3-puntjes menu → &ldquo;App installeren&rdquo; / &ldquo;Toevoegen aan startscherm&rdquo;.
            </p>
          </div>
          <div className="rounded-xl border border-white/10 p-4" data-testid="admin-install-instr-ios">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-4 h-4 text-emerald-300" />
              <div className="text-sm font-semibold text-white">iOS Safari</div>
            </div>
            <p className="text-xs text-white/65 leading-relaxed">
              Deelmenu → &ldquo;Zet op beginscherm&rdquo;. Let op: iOS levert beperkte PWA-functionaliteit.
            </p>
          </div>
        </div>

        {/* Status diagnostiek */}
        <div
          className="rounded-2xl border border-white/10 p-5 sm:p-6"
          style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
          data-testid="admin-install-diag"
        >
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-emerald-300" />
            <div className="text-sm font-semibold text-white">Statuscheck</div>
          </div>
          <StatusRow
            label="Admin-manifest geladen"
            ok={manifestIsAdmin}
            testId="admin-install-diag-manifest"
          />
          <StatusRow
            label="Standalone mode actief"
            ok={diag.standalone}
            testId="admin-install-diag-standalone"
          />
          <StatusRow
            label="Service worker ondersteund"
            ok={diag.sw_available}
            testId="admin-install-diag-sw-available"
          />
          <StatusRow
            label="Service worker geregistreerd"
            ok={diag.sw_registered}
            testId="admin-install-diag-sw-registered"
          />
          <StatusRow
            label="Huidige route is /admin*"
            ok={diag.is_admin_route}
            testId="admin-install-diag-route"
          />

          <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-white/45 leading-snug">
            Apparaattype: <span className="text-white/65">{diag.ua_class}</span> ·
            Manifest: <span className="text-white/65 break-all">{diag.manifest_href || '—'}</span>
          </div>
        </div>

        <p className="text-[11px] text-white/40 mt-6 leading-relaxed max-w-prose">
          Deze pagina is uitsluitend bedoeld voor admin/owner-gebruikers. Niet delen met klanten.
          Geen publieke Play Store-listing. Geen Stripe-, Ads- of mail-mutaties via deze pagina.
        </p>
      </div>
    </div>
  );
}
